import React from "react";
import { useNavigate } from "react-router";
import { DotLegalTextField } from "@dotlegal/dotlegal-ui-components";
import MentionInput from "@common/components/mentionInput/MentionInput.tsx";
import { EntityType } from "@common/Common.types.ts";
import { ValidationError } from "@common/validationError.ts";
import { exhaustiveGuard } from "@common/utilities.ts";
import { isNullOrWhitespace } from "@common/stringOperations.ts";
import { TagRelationType } from "@common/components/tagsMultiSelect/TagsMultiSelect.types.ts";
import TagsMultiSelectStandAlone from "@common/components/tagsMultiSelect/tagsMultiSelectAutoUpdater/TagsMultiSelectAutoUpdater.tsx";
import EntityRelationsTable from "@common/components/entityRelationsTable/EntityRelationsTable.tsx";
import { useTranslation } from "@localization/useTranslation.ts";
import { useUrlProvider } from "../useUrlProvider.ts";
import { EntityTemplateField, IncidentLogStandardField, isIncidentLogStandardField, TemplateEntityType } from "./EntityTemplate.types.ts";
import { InputFieldTypeEnum } from "./inputFields/InputFields.types.ts";
import { CustomFieldValue } from "../system/systemEdit/SystemEdit.types.ts";
import {
    AccessRequestOutcome,
    AccessRequestType,
    IncidentLogEntryStatus,
    IncidentLogNotificationAnswer,
    ReceiptChannel,
    SeverityLevelEnum,
    YesNoAnswer,
    YesNoNotNecessaryAnswer,
    YesNoUnknownAnswer,
    InformationSecurityProperty,
} from "../incidentLog/IncidentLogDialog.types.ts";
import IncidentLogSeverityField from "./fields/incidentLogFields/IncidentLogSeverityField.tsx";
import IncidentLogAccessRequestTypeField from "./fields/incidentLogFields/IncidentLogAccessRequestTypeField.tsx";
import IncidentLogReceiptChannelField from "./fields/incidentLogFields/IncidentLogReceiptChannelField.tsx";
import IncidentLogDeadlineExtendedField from "./fields/incidentLogFields/IncidentLogDeadlineExtendedField.tsx";
import IncidentLogIdentityVerifiedField from "./fields/incidentLogFields/IncidentLogIdentityVerifiedField.tsx";
import IncidentLogRequestMadeThroughRepresentativeField from "./fields/incidentLogFields/IncidentLogRequestMadeThroughRepresentativeField.tsx";
import IncidentLogYesNoAnswerField from "./fields/incidentLogFields/IncidentLogYesNoAnswerField.tsx";
import IncidentLogAccessRequestOutcomeField from "./fields/incidentLogFields/IncidentLogAccessRequestOutcomeField.tsx";
import {
    hasPersonalDataToCopy,
    isAccessRestricted,
    showsCopyOfPersonalDataProvided,
    showsWhenAccessIsRestricted,
} from "./fields/incidentLogFields/incidentLogAccessRequestOutcomeVisibility.ts";
import IncidentLogResponseSentField from "./fields/incidentLogFields/IncidentLogResponseSentField.tsx";
import IncidentLogYesNoUnknownAnswerField from "./fields/incidentLogFields/IncidentLogYesNoUnknownAnswerField.tsx";
import IncidentLogAffectedSecurityPropertiesField from "./fields/incidentLogFields/IncidentLogAffectedSecurityPropertiesField.tsx";
import IncidentLogEstimatedFinancialLossField from "./fields/incidentLogFields/IncidentLogEstimatedFinancialLossField.tsx";
import IncidentLogEstimatedNumberField from "./fields/incidentLogFields/IncidentLogEstimatedNumberField.tsx";
import IncidentLogCrossBorderImpactField from "./fields/incidentLogFields/IncidentLogCrossBorderImpactField.tsx";
import IncidentLogTypesField from "./fields/incidentLogFields/IncidentLogTypesField.tsx";
import IncidentLogAffectedServicesField from "./fields/incidentLogFields/IncidentLogAffectedServicesField.tsx";
import IncidentLogStatusField from "./fields/incidentLogFields/IncidentLogStatusField.tsx";
import IncidentLogExcludedUsersField from "./fields/incidentLogFields/IncidentLogExcludedUsersField.tsx";
import IncidentLogDateTimeField from "./fields/incidentLogFields/IncidentLogDateTimeField.tsx";
import IncidentLogDataCategoriesField from "./fields/incidentLogFields/incidentLogDataCategories/IncidentLogDataCategoriesField.tsx";
import IncidentLogDataSubjectsField from "./fields/incidentLogFields/incidentLogDataSubjects/IncidentLogDataSubjectsField.tsx";
import IncidentLogTextWithSuggestionsField from "./fields/incidentLogFields/IncidentLogTextWithSuggestionsField.tsx";
import IncidentLogMeasuresField from "./fields/incidentLogFields/incidentLogMeasures/IncidentLogMeasuresField.tsx";
import IncidentLogNotificationField from "./fields/incidentLogFields/IncidentLogNotificationField.tsx";
import { changesNotificationDescriptionMeaning } from "./fields/incidentLogFields/incidentLogNotificationVisibility.ts";
import ComplianceAreasField from "./fields/ComplianceAreasField.tsx";
import { getConsequenceSuggestions } from "../incidentLog/incidentLogDialog/CreateEditIncidentLogDialog.hooks.ts";
import IncidentDocuments from "../incidentLog/incidentLogOverview/documents/IncidentDocuments.tsx";
import DocumentsEmptyState from "@common/components/documentsEmptyState/DocumentsEmptyState.tsx";
import AdditionalUserAccessField from "./fields/AdditionalUserAccessField.tsx";
import AccessAwareResponsibleField from "./fields/AccessAwareResponsibleField.tsx";
import VisibleToAllUsersField from "./fields/VisibleToAllUsersField.tsx";
import BusinessAreaAccessField from "./fields/BusinessAreaAccessField.tsx";
import BusinessAreaField from "./fields/BusinessAreaField.tsx";
import GroupCompaniesAccessField from "./fields/GroupCompaniesAccessField.tsx";
import { AccessControlEntity } from "./fields/useAccessResponsibilityHelper.hooks.ts";
import CustomTextField from "./fields/customFields/CustomTextField.tsx";
import CustomRichTextField from "./fields/customFields/CustomRichTextField.tsx";
import CustomCountryField from "./fields/customFields/CustomCountryField.tsx";
import CustomSingleSelectField from "./fields/customFields/CustomSingleSelectField.tsx";
import CustomMultiSelectField from "./fields/customFields/CustomMultiSelectField.tsx";
import CustomDateField from "./fields/customFields/CustomDateField.tsx";
import CustomNumberField from "./fields/customFields/CustomNumberField.tsx";
import CustomAmountField from "./fields/customFields/CustomAmountField.tsx";
import CustomCheckbox from "./fields/customFields/CustomCheckbox.tsx";
import CustomLegalEntityField from "./fields/customFields/CustomLegalEntityField.tsx";
import CustomUserField from "./fields/customFields/CustomUserField.tsx";
import CustomCalculatedField from "./fields/customFields/CustomCalculatedField.tsx";
import CustomDatePeriodField from "./fields/customFields/CustomDatePeriodField.tsx";
import { ComputedFieldTemplateEditing } from "./fields/customFields/computedField.types.ts";

export interface IncidentLogFieldModel extends AccessControlEntity {
    id: string;
    name?: string;
    // The old dialog's main "describe what happened" text, which it called the cause.
    description?: string;
    severity?: SeverityLevelEnum | null;
    accessRequestType?: AccessRequestType | null;
    suspectedUnlawfulOrMaliciousAct?: YesNoUnknownAnswer | null;
    types: Array<string>;
    businessAreaIds: Array<string>;
    complianceAreaIds: Array<string>;
    note?: string;
    status?: IncidentLogEntryStatus;
    discoveryDate?: Date | null;
    discoveryTime?: Date | null;
    receiptDate?: Date | null;
    receiptTime?: Date | null;
    reportedBy?: string;
    receivedBy?: string;
    receiptChannel?: ReceiptChannel | null;
    deadlineExtended?: YesNoAnswer | null;
    deadlineExtensionJustification?: string;
    dataSubjectInformedOfExtensionDate?: Date | null;
    dataSubjectName?: string;
    identityVerified?: YesNoNotNecessaryAnswer | null;
    identityVerificationMethod?: string;
    requestMadeThroughRepresentative?: YesNoAnswer | null;
    representativeName?: string;
    requestedDataCategoryIds: Array<string>;
    accessRequestOutcome?: AccessRequestOutcome | null;
    accessRequestOutcomeJustification?: string;
    responseSent?: YesNoAnswer | null;
    responseDate?: Date | null;
    responseTime?: Date | null;
    responseMethod?: string;
    copyOfPersonalDataProvided?: YesNoAnswer | null;
    informedOfRightToComplain?: YesNoAnswer | null;
    startDate?: Date | null;
    startTime?: Date | null;
    isStartUnknown: boolean;
    endDate?: Date | null;
    endTime?: Date | null;
    isEndDateOngoing: boolean;
    recoveryDate?: Date | null;
    recoveryTime?: Date | null;
    dataCategoryIds: Array<string>;
    dataSubjectIds: Array<string>;
    estimatedNumberOfAffectedPeople?: number | null;
    isEstimatedNumberOfAffectedPeopleUnknown: boolean;
    estimatedNumberOfAffectedRecords?: number | null;
    isEstimatedNumberOfAffectedRecordsUnknown: boolean;
    affectedServiceIds: Array<string>;
    affectedSecurityProperties: Array<InformationSecurityProperty>;
    personalDataInvolved?: YesNoUnknownAnswer | null;
    estimatedNumberOfAffectedServiceRecipients?: number | null;
    estimatedFinancialLoss?: number | null;
    estimatedFinancialLossCurrencyCode?: string | null;
    crossBorderImpact?: YesNoUnknownAnswer | null;
    affectedCountryCodes: Array<string>;
    consequences?: string;
    measures?: string;
    reportedToAgency?: IncidentLogNotificationAnswer | null;
    reportedToAgencyDate?: Date | null;
    reportedToAgencyTime?: Date | null;
    reportedToAgencyDescription?: string;
    affectedPeopleNotified?: IncidentLogNotificationAnswer | null;
    affectedPeopleNotifiedDate?: Date | null;
    affectedPeopleNotifiedTime?: Date | null;
    affectedPeopleNotifiedDescription?: string;
    significantIncidentAwarenessDate?: Date | null;
    significantIncidentAwarenessTime?: Date | null;
    supplierNotificationDate?: Date | null;
    supplierNotificationTime?: Date | null;
    reportedToAuthorities?: IncidentLogNotificationAnswer | null;
    earlyWarningDate?: Date | null;
    earlyWarningTime?: Date | null;
    incidentNotificationDate?: Date | null;
    incidentNotificationTime?: Date | null;
    finalReportDate?: Date | null;
    finalReportTime?: Date | null;
    reportedToAuthoritiesDescription?: string;
    serviceRecipientsNotified?: IncidentLogNotificationAnswer | null;
    serviceRecipientsNotifiedDate?: Date | null;
    serviceRecipientsNotifiedTime?: Date | null;
    serviceRecipientsNotifiedDescription?: string;
    informationSecurityReportedToAuthorities?: IncidentLogNotificationAnswer | null;
    informationSecurityReportedToAuthoritiesDate?: Date | null;
    informationSecurityReportedToAuthoritiesTime?: Date | null;
    informationSecurityReportedToAuthoritiesDescription?: string;
    affectedPartiesNotified?: IncidentLogNotificationAnswer | null;
    affectedPartiesNotifiedDate?: Date | null;
    affectedPartiesNotifiedTime?: Date | null;
    affectedPartiesNotifiedDescription?: string;
    entityTemplateId?: string;
    customFieldValues: Array<CustomFieldValue>;
}

const incidentLogCustomFieldApiPath = "incident-log-entries";

export interface IncidentLogStandardFieldProps {
    field: EntityTemplateField;
    isLoading: boolean;
    incidentLog?: IncidentLogFieldModel;
    incidentLogId: string;
    onChange: (changeFunc: (incidentLog: IncidentLogFieldModel) => void) => Promise<void>;
    validationErrors: Array<ValidationError>;
    isPreview: boolean;
    disableAccessWarning?: boolean;
}

export interface IncidentLogCustomFieldProps {
    incidentLogId: string;
    incidentLog?: IncidentLogFieldModel;
    field: EntityTemplateField;
    onChange: (changeFunc: (incidentLog: IncidentLogFieldModel) => void) => void;
    isPreview: boolean;
    isLoading: boolean;
    validationErrors: Array<ValidationError>;
    computedFieldEditing?: ComputedFieldTemplateEditing; // For template editor preview
}

export interface IncidentLogFieldRendererProps {
    field: EntityTemplateField;
    incidentLog?: IncidentLogFieldModel;
    isLoading: boolean;
    incidentLogId?: string;
    onChange: (changeFunc: (incidentLog: IncidentLogFieldModel) => void) => Promise<void>;
    validationErrors?: Array<ValidationError>;
    onCustomFieldChange: (changeFunc: (incidentLog: IncidentLogFieldModel) => void) => void;
    isPreview: boolean;
    computedFieldEditing?: ComputedFieldTemplateEditing; // For template editor preview
}

export const IncidentLogStandardFieldRenderer: React.FC<IncidentLogStandardFieldProps> = ({
    field,
    incidentLog,
    incidentLogId,
    onChange,
    validationErrors,
    isLoading,
    isPreview,
    disableAccessWarning,
}) => {
    const { translateString } = useTranslation();
    const navigate = useNavigate();
    const urlProvider = useUrlProvider();
    if (!field.standardFieldIdentifier || !isIncidentLogStandardField(field.standardFieldIdentifier)) {
        return null;
    }

    const isDisabled = field.disabled || isPreview;
    const getErrorText = (standardField: IncidentLogStandardField) => validationErrors.find((v) => v.field === IncidentLogStandardField[standardField])?.error;

    switch (field.standardFieldIdentifier) {
        case IncidentLogStandardField.IncidentLogName:
            return (
                <DotLegalTextField
                    label={translateString("name")}
                    placeholder={translateString("incidentNameTextFieldPlaceholder")}
                    value={incidentLog?.name ?? ""}
                    debounce
                    onChange={(value) =>
                        onChange((entity) => {
                            entity.name = value;
                        })
                    }
                    disabled={isDisabled}
                    errorText={getErrorText(IncidentLogStandardField.IncidentLogName)}
                />
            );
        case IncidentLogStandardField.IncidentLogDescription:
            return (
                <MentionInput
                    multiline
                    rows={6}
                    label={translateString("description")}
                    placeholder={translateString("incidentCauseTextFieldPlaceholder")}
                    value={incidentLog?.description ?? ""}
                    debounce
                    onChange={(value) =>
                        onChange((entity) => {
                            entity.description = value;
                        })
                    }
                    disabled={isDisabled}
                    errorText={getErrorText(IncidentLogStandardField.IncidentLogDescription)}
                    {...(isNullOrWhitespace(incidentLogId)
                        ? { disableMentions: true as const }
                        : { mentionSource: { entityFromId: incidentLogId, entityFromType: EntityType.IncidentLog } })}
                />
            );
        case IncidentLogStandardField.IncidentLogNote:
            return (
                <MentionInput
                    multiline
                    rows={6}
                    label={translateString("note")}
                    value={incidentLog?.note ?? ""}
                    debounce
                    onChange={(value) =>
                        onChange((entity) => {
                            entity.note = value;
                        })
                    }
                    disabled={isDisabled}
                    errorText={getErrorText(IncidentLogStandardField.IncidentLogNote)}
                    {...(isNullOrWhitespace(incidentLogId)
                        ? { disableMentions: true as const }
                        : { mentionSource: { entityFromId: incidentLogId, entityFromType: EntityType.IncidentLog } })}
                />
            );
        case IncidentLogStandardField.IncidentLogSeverity:
            return (
                <IncidentLogSeverityField
                    severity={incidentLog?.severity}
                    onChange={(severity) =>
                        onChange((entity) => {
                            entity.severity = severity;
                        })
                    }
                    disabled={isDisabled}
                    errorText={getErrorText(IncidentLogStandardField.IncidentLogSeverity)}
                />
            );
        case IncidentLogStandardField.IncidentLogAccessRequestType:
            return (
                <IncidentLogAccessRequestTypeField
                    accessRequestType={incidentLog?.accessRequestType}
                    onChange={(accessRequestType) =>
                        onChange((entity) => {
                            entity.accessRequestType = accessRequestType;
                        })
                    }
                    disabled={isDisabled}
                    errorText={getErrorText(IncidentLogStandardField.IncidentLogAccessRequestType)}
                />
            );
        case IncidentLogStandardField.IncidentLogSuspectedUnlawfulOrMaliciousAct:
            return (
                <IncidentLogYesNoUnknownAnswerField
                    label={translateString("suspectedUnlawfulOrMaliciousAct")}
                    answer={incidentLog?.suspectedUnlawfulOrMaliciousAct}
                    onChange={(answer) =>
                        onChange((entity) => {
                            entity.suspectedUnlawfulOrMaliciousAct = answer;
                        })
                    }
                    disabled={isDisabled}
                    errorText={getErrorText(IncidentLogStandardField.IncidentLogSuspectedUnlawfulOrMaliciousAct)}
                />
            );
        case IncidentLogStandardField.IncidentLogTypes:
            return (
                <IncidentLogTypesField
                    types={incidentLog?.types ?? []}
                    onTypesChange={(types) =>
                        onChange((entity) => {
                            entity.types = types;
                        })
                    }
                    isPreview={isPreview}
                    disabled={isDisabled}
                    errorText={getErrorText(IncidentLogStandardField.IncidentLogTypes)}
                />
            );
        case IncidentLogStandardField.IncidentLogBusinessAreas:
            return (
                <BusinessAreaField
                    businessAreas={incidentLog?.businessAreaIds ?? []}
                    isLoading={isLoading}
                    onChange={(businessAreaIds) =>
                        onChange((entity) => {
                            entity.businessAreaIds = businessAreaIds;
                        })
                    }
                    disabled={isDisabled}
                    isPreview={isPreview}
                    errorText={getErrorText(IncidentLogStandardField.IncidentLogBusinessAreas)}
                />
            );
        case IncidentLogStandardField.IncidentLogComplianceAreas:
            return (
                <ComplianceAreasField
                    complianceAreaIds={incidentLog?.complianceAreaIds ?? []}
                    onChange={(complianceAreaIds) =>
                        onChange((entity) => {
                            entity.complianceAreaIds = complianceAreaIds;
                        })
                    }
                    disabled={isDisabled}
                    isPreview={isPreview}
                    errorText={getErrorText(IncidentLogStandardField.IncidentLogComplianceAreas)}
                />
            );
        case IncidentLogStandardField.IncidentLogTags:
            return (
                <TagsMultiSelectStandAlone
                    entityId={incidentLogId}
                    isLoading={false}
                    entityType={TagRelationType.IncidentLog}
                    disabled={isDisabled}
                    errorText={getErrorText(IncidentLogStandardField.IncidentLogTags)}
                    disableQuery={isPreview}
                />
            );
        case IncidentLogStandardField.IncidentLogStatus:
            return (
                <IncidentLogStatusField
                    status={incidentLog?.status}
                    onChange={(status) =>
                        onChange((entity) => {
                            entity.status = status;
                        })
                    }
                    disabled={isDisabled}
                    errorText={getErrorText(IncidentLogStandardField.IncidentLogStatus)}
                />
            );
        case IncidentLogStandardField.IncidentLogResponsible:
            return (
                <AccessAwareResponsibleField
                    entity={incidentLog}
                    isLoading={isLoading}
                    onChange={onChange}
                    onConfirmNavigate={() => navigate(urlProvider.getIncidentLogUrl())}
                    disabled={isDisabled}
                    isPreview={isPreview}
                    toolTipText={translateString("incidentLogResponsibleTooltip")}
                    warningText={isPreview ? undefined : translateString("incidentLogResponsibleWarning")}
                    errorText={getErrorText(IncidentLogStandardField.IncidentLogResponsible)}
                />
            );
        case IncidentLogStandardField.IncidentLogAdditionalUserAccess:
            return (
                <AdditionalUserAccessField
                    entity={incidentLog}
                    isLoading={isLoading}
                    onChange={onChange}
                    onConfirmNavigate={() => navigate(urlProvider.getIncidentLogUrl())}
                    disabled={isDisabled}
                    isPreview={isPreview}
                    toolTipText={translateString("incidentLogAdditionalAccessTooltip")}
                    warningText={translateString("incidentLogAdditionalAccessWarning")}
                    disableAccessWarning={disableAccessWarning}
                    errorText={getErrorText(IncidentLogStandardField.IncidentLogAdditionalUserAccess)}
                />
            );
        case IncidentLogStandardField.IncidentLogExcludedUsers:
            return (
                <IncidentLogExcludedUsersField
                    incidentLogId={incidentLogId}
                    incidentLog={incidentLog}
                    isLoading={isLoading}
                    onChange={onChange}
                    disabled={isDisabled}
                    isPreview={isPreview}
                    errorText={getErrorText(IncidentLogStandardField.IncidentLogExcludedUsers)}
                />
            );
        case IncidentLogStandardField.IncidentLogVisibleToAllUsers:
            return (
                <VisibleToAllUsersField
                    entity={incidentLog}
                    onChange={onChange}
                    onConfirmNavigate={() => navigate(urlProvider.getIncidentLogUrl())}
                    disabled={isDisabled}
                    isPreview={isPreview}
                    toolTipText={translateString("visibleToAllUsersTooltip")}
                    confirmAccessRemovalKey="confirmIncidentLogAccessRemovalBody"
                    disableAccessWarning={disableAccessWarning}
                    errorText={getErrorText(IncidentLogStandardField.IncidentLogVisibleToAllUsers)}
                />
            );
        case IncidentLogStandardField.IncidentLogBusinessAreaAccess:
            return (
                <BusinessAreaAccessField
                    entity={incidentLog}
                    isLoading={isLoading}
                    onChange={onChange}
                    onConfirmNavigate={() => navigate(urlProvider.getIncidentLogUrl())}
                    disabled={isDisabled}
                    isPreview={isPreview}
                    toolTipText={translateString("incidentLogBusinessAreaAccessTooltip")}
                    warningText={translateString("incidentLogBusinessAreaAccessWarning")}
                    disableAccessWarning={disableAccessWarning}
                    errorText={getErrorText(IncidentLogStandardField.IncidentLogBusinessAreaAccess)}
                />
            );
        case IncidentLogStandardField.IncidentLogGroupCompanies:
            return (
                <GroupCompaniesAccessField
                    entity={incidentLog}
                    isLoading={isLoading}
                    onChange={onChange}
                    onConfirmNavigate={() => navigate(urlProvider.getIncidentLogUrl())}
                    addGroupEntityDialogHeader={translateString("addCompaniesToIncidentLog")}
                    disabled={isDisabled}
                    isPreview={isPreview}
                    confirmAccessRemovalKey="confirmIncidentLogAccessRemovalBody"
                    disableAccessWarning={disableAccessWarning}
                    errorText={getErrorText(IncidentLogStandardField.IncidentLogGroupCompanies)}
                />
            );
        case IncidentLogStandardField.IncidentLogDiscoveryDate:
            return (
                <IncidentLogDateTimeField
                    label={translateString("discoveryDate")}
                    toolTipText={translateString("discoveryDateHelpText")}
                    date={incidentLog?.discoveryDate}
                    time={incidentLog?.discoveryTime}
                    onDateChange={(date) =>
                        onChange((entity) => {
                            entity.discoveryDate = date;
                        })
                    }
                    onTimeChange={(time) =>
                        onChange((entity) => {
                            entity.discoveryTime = time;
                        })
                    }
                    disabled={isDisabled}
                    errorText={getErrorText(IncidentLogStandardField.IncidentLogDiscoveryDate)}
                >
                    <DotLegalTextField
                        label={translateString("reportedBy")}
                        toolTipText={translateString("reportedByHelpText")}
                        value={incidentLog?.reportedBy ?? ""}
                        debounce
                        onChange={(value) =>
                            onChange((entity) => {
                                entity.reportedBy = value;
                            })
                        }
                        disabled={isDisabled}
                    />
                </IncidentLogDateTimeField>
            );
        case IncidentLogStandardField.IncidentLogReceiptDate:
            return (
                <IncidentLogDateTimeField
                    label={translateString("receiptDate")}
                    date={incidentLog?.receiptDate}
                    time={incidentLog?.receiptTime}
                    onDateChange={(date) =>
                        onChange((entity) => {
                            entity.receiptDate = date;
                        })
                    }
                    onTimeChange={(time) =>
                        onChange((entity) => {
                            entity.receiptTime = time;
                        })
                    }
                    disabled={isDisabled}
                    errorText={getErrorText(IncidentLogStandardField.IncidentLogReceiptDate)}
                >
                    <DotLegalTextField
                        label={translateString("receivedBy")}
                        value={incidentLog?.receivedBy ?? ""}
                        debounce
                        onChange={(value) =>
                            onChange((entity) => {
                                entity.receivedBy = value;
                            })
                        }
                        disabled={isDisabled}
                    />
                    <IncidentLogReceiptChannelField
                        receiptChannel={incidentLog?.receiptChannel}
                        onChange={(receiptChannel) =>
                            onChange((entity) => {
                                entity.receiptChannel = receiptChannel;
                            })
                        }
                        disabled={isDisabled}
                    />
                </IncidentLogDateTimeField>
            );
        case IncidentLogStandardField.IncidentLogSignificantIncidentAwarenessDate:
            return (
                <IncidentLogDateTimeField
                    label={translateString("significantIncidentAwarenessDate")}
                    toolTipText={translateString("significantIncidentAwarenessDateHelpText")}
                    date={incidentLog?.significantIncidentAwarenessDate}
                    time={incidentLog?.significantIncidentAwarenessTime}
                    onDateChange={(date) =>
                        onChange((entity) => {
                            entity.significantIncidentAwarenessDate = date;
                        })
                    }
                    onTimeChange={(time) =>
                        onChange((entity) => {
                            entity.significantIncidentAwarenessTime = time;
                        })
                    }
                    disabled={isDisabled}
                    errorText={getErrorText(IncidentLogStandardField.IncidentLogSignificantIncidentAwarenessDate)}
                />
            );
        case IncidentLogStandardField.IncidentLogStartDate:
            return (
                <IncidentLogDateTimeField
                    label={translateString("incidentTime")}
                    toolTipText={translateString("incidentTimeHelpText")}
                    date={incidentLog?.startDate}
                    time={incidentLog?.startTime}
                    onDateChange={(date) =>
                        onChange((entity) => {
                            entity.startDate = date;
                        })
                    }
                    onTimeChange={(time) =>
                        onChange((entity) => {
                            entity.startTime = time;
                        })
                    }
                    noDateCheckbox={{
                        label: translateString("unknown"),
                        checked: incidentLog?.isStartUnknown ?? false,
                        onChange: (isUnknown) =>
                            onChange((entity) => {
                                entity.isStartUnknown = isUnknown;
                                if (isUnknown) {
                                    entity.startDate = null;
                                    entity.startTime = null;
                                }
                            }),
                    }}
                    disabled={isDisabled}
                    errorText={getErrorText(IncidentLogStandardField.IncidentLogStartDate)}
                />
            );
        case IncidentLogStandardField.IncidentLogEndDate:
            return (
                <IncidentLogDateTimeField
                    label={translateString("incidentEndTime")}
                    toolTipText={translateString("incidentEndTimeHelpText")}
                    date={incidentLog?.endDate}
                    time={incidentLog?.endTime}
                    onDateChange={(date) =>
                        onChange((entity) => {
                            entity.endDate = date;
                        })
                    }
                    onTimeChange={(time) =>
                        onChange((entity) => {
                            entity.endTime = time;
                        })
                    }
                    noDateCheckbox={{
                        label: translateString("ongoing"),
                        checked: incidentLog?.isEndDateOngoing ?? false,
                        onChange: (isOngoing) =>
                            onChange((entity) => {
                                entity.isEndDateOngoing = isOngoing;
                                if (isOngoing) {
                                    entity.endDate = null;
                                    entity.endTime = null;
                                }
                            }),
                    }}
                    disabled={isDisabled}
                    errorText={getErrorText(IncidentLogStandardField.IncidentLogEndDate)}
                />
            );
        case IncidentLogStandardField.IncidentLogRecoveryDate:
            return (
                <IncidentLogDateTimeField
                    label={translateString("recoveryDate")}
                    toolTipText={translateString("recoveryDateHelpText")}
                    date={incidentLog?.recoveryDate}
                    time={incidentLog?.recoveryTime}
                    onDateChange={(date) =>
                        onChange((entity) => {
                            entity.recoveryDate = date;
                        })
                    }
                    onTimeChange={(time) =>
                        onChange((entity) => {
                            entity.recoveryTime = time;
                        })
                    }
                    disabled={isDisabled}
                    errorText={getErrorText(IncidentLogStandardField.IncidentLogRecoveryDate)}
                />
            );
        case IncidentLogStandardField.IncidentLogSupplierNotificationDate:
            return (
                <IncidentLogDateTimeField
                    label={translateString("supplierNotificationDate")}
                    toolTipText={translateString("supplierNotificationDateHelpText")}
                    date={incidentLog?.supplierNotificationDate}
                    time={incidentLog?.supplierNotificationTime}
                    onDateChange={(date) =>
                        onChange((entity) => {
                            entity.supplierNotificationDate = date;
                        })
                    }
                    onTimeChange={(time) =>
                        onChange((entity) => {
                            entity.supplierNotificationTime = time;
                        })
                    }
                    disabled={isDisabled}
                    errorText={getErrorText(IncidentLogStandardField.IncidentLogSupplierNotificationDate)}
                />
            );
        case IncidentLogStandardField.IncidentLogDeadlineExtended:
            return (
                <IncidentLogDeadlineExtendedField
                    answer={incidentLog?.deadlineExtended}
                    justification={incidentLog?.deadlineExtensionJustification}
                    dataSubjectInformedDate={incidentLog?.dataSubjectInformedOfExtensionDate}
                    onAnswerChange={(answer) =>
                        onChange((entity) => {
                            entity.deadlineExtended = answer;
                            if (answer !== YesNoAnswer.Yes) {
                                entity.deadlineExtensionJustification = "";
                                entity.dataSubjectInformedOfExtensionDate = null;
                            }
                        })
                    }
                    onJustificationChange={(justification) =>
                        onChange((entity) => {
                            entity.deadlineExtensionJustification = justification;
                        })
                    }
                    onDataSubjectInformedDateChange={(date) =>
                        onChange((entity) => {
                            entity.dataSubjectInformedOfExtensionDate = date;
                        })
                    }
                    isPreview={isPreview}
                    disabled={isDisabled}
                    errorText={getErrorText(IncidentLogStandardField.IncidentLogDeadlineExtended)}
                />
            );
        case IncidentLogStandardField.IncidentLogDataSubjectName:
            return (
                <DotLegalTextField
                    label={translateString("dataSubjectName")}
                    value={incidentLog?.dataSubjectName ?? ""}
                    debounce
                    onChange={(value) =>
                        onChange((entity) => {
                            entity.dataSubjectName = value;
                        })
                    }
                    disabled={isDisabled}
                    errorText={getErrorText(IncidentLogStandardField.IncidentLogDataSubjectName)}
                />
            );
        case IncidentLogStandardField.IncidentLogIdentityVerified:
            return (
                <IncidentLogIdentityVerifiedField
                    answer={incidentLog?.identityVerified}
                    verificationMethod={incidentLog?.identityVerificationMethod}
                    onAnswerChange={(answer) =>
                        onChange((entity) => {
                            entity.identityVerified = answer;
                            if (answer !== YesNoNotNecessaryAnswer.Yes) {
                                entity.identityVerificationMethod = "";
                            }
                        })
                    }
                    onVerificationMethodChange={(verificationMethod) =>
                        onChange((entity) => {
                            entity.identityVerificationMethod = verificationMethod;
                        })
                    }
                    isPreview={isPreview}
                    disabled={isDisabled}
                    errorText={getErrorText(IncidentLogStandardField.IncidentLogIdentityVerified)}
                />
            );
        case IncidentLogStandardField.IncidentLogRequestMadeThroughRepresentative:
            return (
                <IncidentLogRequestMadeThroughRepresentativeField
                    answer={incidentLog?.requestMadeThroughRepresentative}
                    representativeName={incidentLog?.representativeName}
                    onAnswerChange={(answer) =>
                        onChange((entity) => {
                            entity.requestMadeThroughRepresentative = answer;
                            if (answer !== YesNoAnswer.Yes) {
                                entity.representativeName = "";
                            }
                        })
                    }
                    onRepresentativeNameChange={(representativeName) =>
                        onChange((entity) => {
                            entity.representativeName = representativeName;
                        })
                    }
                    isPreview={isPreview}
                    disabled={isDisabled}
                    errorText={getErrorText(IncidentLogStandardField.IncidentLogRequestMadeThroughRepresentative)}
                />
            );
        case IncidentLogStandardField.IncidentLogRequestedDataCategories:
            return (
                <IncidentLogDataCategoriesField
                    label={translateString("requestedDataCategories")}
                    dataCategoryIds={incidentLog?.requestedDataCategoryIds ?? []}
                    onChange={(dataCategoryIds) =>
                        onChange((entity) => {
                            entity.requestedDataCategoryIds = dataCategoryIds;
                        })
                    }
                    isLoading={isLoading}
                    isPreview={isPreview}
                    disabled={isDisabled}
                    errorText={getErrorText(IncidentLogStandardField.IncidentLogRequestedDataCategories)}
                />
            );
        case IncidentLogStandardField.IncidentLogAccessRequestOutcome:
            return (
                <IncidentLogAccessRequestOutcomeField
                    outcome={incidentLog?.accessRequestOutcome}
                    justification={incidentLog?.accessRequestOutcomeJustification}
                    onOutcomeChange={(outcome) =>
                        onChange((entity) => {
                            entity.accessRequestOutcome = outcome;
                            if (!isAccessRestricted(outcome)) {
                                entity.accessRequestOutcomeJustification = "";
                                entity.informedOfRightToComplain = null;
                            }
                            if (!hasPersonalDataToCopy(outcome)) {
                                entity.copyOfPersonalDataProvided = null;
                            }
                        })
                    }
                    onJustificationChange={(justification) =>
                        onChange((entity) => {
                            entity.accessRequestOutcomeJustification = justification;
                        })
                    }
                    isPreview={isPreview}
                    disabled={isDisabled}
                    errorText={getErrorText(IncidentLogStandardField.IncidentLogAccessRequestOutcome)}
                />
            );
        case IncidentLogStandardField.IncidentLogResponseSent:
            return (
                <IncidentLogResponseSentField
                    answer={incidentLog?.responseSent}
                    date={incidentLog?.responseDate}
                    time={incidentLog?.responseTime}
                    method={incidentLog?.responseMethod}
                    onAnswerChange={(answer) =>
                        onChange((entity) => {
                            entity.responseSent = answer;
                            if (answer !== YesNoAnswer.Yes) {
                                entity.responseDate = null;
                                entity.responseTime = null;
                                entity.responseMethod = "";
                            }
                        })
                    }
                    onDateChange={(date) =>
                        onChange((entity) => {
                            entity.responseDate = date;
                        })
                    }
                    onTimeChange={(time) =>
                        onChange((entity) => {
                            entity.responseTime = time;
                        })
                    }
                    onMethodChange={(method) =>
                        onChange((entity) => {
                            entity.responseMethod = method;
                        })
                    }
                    isPreview={isPreview}
                    disabled={isDisabled}
                    errorText={getErrorText(IncidentLogStandardField.IncidentLogResponseSent)}
                />
            );
        case IncidentLogStandardField.IncidentLogCopyOfPersonalDataProvided:
            if (!showsCopyOfPersonalDataProvided(incidentLog?.accessRequestOutcome, isPreview)) {
                return null;
            }
            return (
                <IncidentLogYesNoAnswerField
                    label={translateString("copyOfPersonalDataProvided")}
                    answer={incidentLog?.copyOfPersonalDataProvided}
                    onChange={(answer) =>
                        onChange((entity) => {
                            entity.copyOfPersonalDataProvided = answer;
                        })
                    }
                    disabled={isDisabled}
                    errorText={getErrorText(IncidentLogStandardField.IncidentLogCopyOfPersonalDataProvided)}
                />
            );
        case IncidentLogStandardField.IncidentLogInformedOfRightToComplain:
            if (!showsWhenAccessIsRestricted(incidentLog?.accessRequestOutcome, isPreview)) {
                return null;
            }
            return (
                <IncidentLogYesNoAnswerField
                    label={translateString("informedOfRightToComplain")}
                    answer={incidentLog?.informedOfRightToComplain}
                    onChange={(answer) =>
                        onChange((entity) => {
                            entity.informedOfRightToComplain = answer;
                        })
                    }
                    disabled={isDisabled}
                    errorText={getErrorText(IncidentLogStandardField.IncidentLogInformedOfRightToComplain)}
                />
            );
        case IncidentLogStandardField.IncidentLogDataCategories:
            return (
                <IncidentLogDataCategoriesField
                    label={translateString("incidentTableDataCategories")}
                    dataCategoryIds={incidentLog?.dataCategoryIds ?? []}
                    onChange={(dataCategoryIds) =>
                        onChange((entity) => {
                            entity.dataCategoryIds = dataCategoryIds;
                        })
                    }
                    isLoading={isLoading}
                    isPreview={isPreview}
                    disabled={isDisabled}
                    errorText={getErrorText(IncidentLogStandardField.IncidentLogDataCategories)}
                />
            );
        case IncidentLogStandardField.IncidentLogDataSubjects:
            return (
                <IncidentLogDataSubjectsField
                    dataSubjectIds={incidentLog?.dataSubjectIds ?? []}
                    onChange={(dataSubjectIds) =>
                        onChange((entity) => {
                            entity.dataSubjectIds = dataSubjectIds;
                        })
                    }
                    isLoading={isLoading}
                    isPreview={isPreview}
                    disabled={isDisabled}
                    errorText={getErrorText(IncidentLogStandardField.IncidentLogDataSubjects)}
                />
            );
        case IncidentLogStandardField.IncidentLogEstimatedNumberOfAffectedPeople:
            return (
                <IncidentLogEstimatedNumberField
                    label={translateString("estimatedNumberOfAffectedPeople")}
                    toolTipText={translateString("estimatedNumberOfAffectedPeopleHelpText")}
                    value={incidentLog?.estimatedNumberOfAffectedPeople}
                    isUnknown={incidentLog?.isEstimatedNumberOfAffectedPeopleUnknown ?? false}
                    onChange={(value, isUnknown) =>
                        onChange((entity) => {
                            entity.estimatedNumberOfAffectedPeople = value;
                            entity.isEstimatedNumberOfAffectedPeopleUnknown = isUnknown;
                        })
                    }
                    disabled={isDisabled}
                    errorText={getErrorText(IncidentLogStandardField.IncidentLogEstimatedNumberOfAffectedPeople)}
                />
            );
        case IncidentLogStandardField.IncidentLogEstimatedNumberOfAffectedRecords:
            return (
                <IncidentLogEstimatedNumberField
                    label={translateString("estimatedNumberOfAffectedRecords")}
                    toolTipText={translateString("estimatedNumberOfAffectedRecordsHelpText")}
                    value={incidentLog?.estimatedNumberOfAffectedRecords}
                    isUnknown={incidentLog?.isEstimatedNumberOfAffectedRecordsUnknown ?? false}
                    onChange={(value, isUnknown) =>
                        onChange((entity) => {
                            entity.estimatedNumberOfAffectedRecords = value;
                            entity.isEstimatedNumberOfAffectedRecordsUnknown = isUnknown;
                        })
                    }
                    disabled={isDisabled}
                    errorText={getErrorText(IncidentLogStandardField.IncidentLogEstimatedNumberOfAffectedRecords)}
                />
            );
        case IncidentLogStandardField.IncidentLogAffectedSecurityProperties:
            return (
                <IncidentLogAffectedSecurityPropertiesField
                    affectedSecurityProperties={incidentLog?.affectedSecurityProperties ?? []}
                    onChange={(affectedSecurityProperties) =>
                        onChange((entity) => {
                            entity.affectedSecurityProperties = affectedSecurityProperties;
                        })
                    }
                    disabled={isDisabled}
                    errorText={getErrorText(IncidentLogStandardField.IncidentLogAffectedSecurityProperties)}
                />
            );
        case IncidentLogStandardField.IncidentLogPersonalDataInvolved:
            return (
                <IncidentLogYesNoUnknownAnswerField
                    label={translateString("personalDataInvolved")}
                    answer={incidentLog?.personalDataInvolved}
                    onChange={(answer) =>
                        onChange((entity) => {
                            entity.personalDataInvolved = answer;
                        })
                    }
                    disabled={isDisabled}
                    errorText={getErrorText(IncidentLogStandardField.IncidentLogPersonalDataInvolved)}
                />
            );
        case IncidentLogStandardField.IncidentLogAffectedServices:
            return (
                <IncidentLogAffectedServicesField
                    affectedServiceIds={incidentLog?.affectedServiceIds ?? []}
                    onChange={(affectedServiceIds) =>
                        onChange((entity) => {
                            entity.affectedServiceIds = affectedServiceIds;
                        })
                    }
                    isPreview={isPreview}
                    disabled={isDisabled}
                    errorText={getErrorText(IncidentLogStandardField.IncidentLogAffectedServices)}
                />
            );
        case IncidentLogStandardField.IncidentLogEstimatedNumberOfAffectedServiceRecipients:
            return (
                <DotLegalTextField
                    label={translateString("estimatedNumberOfAffectedServiceRecipients")}
                    value={incidentLog?.estimatedNumberOfAffectedServiceRecipients?.toString() ?? ""}
                    onlyNumber="integer"
                    debounce
                    onChange={(value) =>
                        onChange((entity) => {
                            entity.estimatedNumberOfAffectedServiceRecipients = isNullOrWhitespace(value) ? null : Number(value);
                        })
                    }
                    disabled={isDisabled}
                    errorText={getErrorText(IncidentLogStandardField.IncidentLogEstimatedNumberOfAffectedServiceRecipients)}
                />
            );
        case IncidentLogStandardField.IncidentLogEstimatedFinancialLoss:
            return (
                <IncidentLogEstimatedFinancialLossField
                    amount={incidentLog?.estimatedFinancialLoss}
                    currencyCode={incidentLog?.estimatedFinancialLossCurrencyCode}
                    onChange={(amount, currencyCode) =>
                        onChange((entity) => {
                            entity.estimatedFinancialLoss = amount;
                            entity.estimatedFinancialLossCurrencyCode = currencyCode;
                        })
                    }
                    isPreview={isPreview}
                    disabled={isDisabled}
                    errorText={getErrorText(IncidentLogStandardField.IncidentLogEstimatedFinancialLoss)}
                />
            );
        case IncidentLogStandardField.IncidentLogCrossBorderImpact:
            return (
                <IncidentLogCrossBorderImpactField
                    answer={incidentLog?.crossBorderImpact}
                    affectedCountryCodes={incidentLog?.affectedCountryCodes ?? []}
                    onAnswerChange={(answer) =>
                        onChange((entity) => {
                            entity.crossBorderImpact = answer;
                            if (answer !== YesNoUnknownAnswer.Yes) {
                                entity.affectedCountryCodes = [];
                            }
                        })
                    }
                    onAffectedCountriesChange={(countryCodes) =>
                        onChange((entity) => {
                            entity.affectedCountryCodes = countryCodes;
                        })
                    }
                    isPreview={isPreview}
                    disabled={isDisabled}
                    errorText={getErrorText(IncidentLogStandardField.IncidentLogCrossBorderImpact)}
                />
            );
        case IncidentLogStandardField.IncidentLogRelations:
            // Relations are saved against the incident id through their own endpoints, so they are not part of the model.
            return <EntityRelationsTable entityId={incidentLogId} entityType={EntityType.IncidentLog} canEdit={!field.disabled} />;
        case IncidentLogStandardField.IncidentLogConsequences:
            return (
                <IncidentLogTextWithSuggestionsField
                    label={translateString("incidentConsequence")}
                    placeholder={translateString("incidentConsequenceTextFieldPlaceholder")}
                    suggestionsHeader={translateString("incidentConsequenceSuggestions")}
                    suggestions={getConsequenceSuggestions(translateString)}
                    isPreview={isPreview}
                    value={incidentLog?.consequences}
                    onChange={(consequences) =>
                        onChange((entity) => {
                            entity.consequences = consequences;
                        })
                    }
                    disabled={isDisabled}
                    errorText={getErrorText(IncidentLogStandardField.IncidentLogConsequences)}
                />
            );
        case IncidentLogStandardField.IncidentLogMeasures:
            return (
                <IncidentLogMeasuresField
                    measures={incidentLog?.measures}
                    onChange={(measures) =>
                        onChange((entity) => {
                            entity.measures = measures;
                        })
                    }
                    isPreview={isPreview}
                    disabled={isDisabled}
                    errorText={getErrorText(IncidentLogStandardField.IncidentLogMeasures)}
                />
            );
        case IncidentLogStandardField.IncidentLogReportedToAgency:
            return (
                <IncidentLogNotificationField
                    answerLabel={translateString("reportToAuthorities")}
                    givenDescriptionLabel={translateString("reportingMethod")}
                    notGivenDescriptionLabel={translateString("reasonForNotReporting")}
                    answer={incidentLog?.reportedToAgency}
                    dates={[
                        {
                            label: translateString("dateAndTimeOfReport"),
                            date: incidentLog?.reportedToAgencyDate,
                            time: incidentLog?.reportedToAgencyTime,
                            onDateChange: (date) =>
                                onChange((entity) => {
                                    entity.reportedToAgencyDate = date;
                                }),
                            onTimeChange: (time) =>
                                onChange((entity) => {
                                    entity.reportedToAgencyTime = time;
                                }),
                        },
                    ]}
                    description={incidentLog?.reportedToAgencyDescription}
                    onAnswerChange={(answer) =>
                        onChange((entity) => {
                            if (changesNotificationDescriptionMeaning(entity.reportedToAgency, answer)) {
                                entity.reportedToAgencyDescription = "";
                            }
                            entity.reportedToAgency = answer;
                            if (answer !== IncidentLogNotificationAnswer.Yes) {
                                entity.reportedToAgencyDate = null;
                                entity.reportedToAgencyTime = null;
                            }
                        })
                    }
                    onDescriptionChange={(description) =>
                        onChange((entity) => {
                            entity.reportedToAgencyDescription = description;
                        })
                    }
                    isPreview={isPreview}
                    disabled={isDisabled}
                    errorText={getErrorText(IncidentLogStandardField.IncidentLogReportedToAgency)}
                />
            );
        case IncidentLogStandardField.IncidentLogAffectedPeopleNotified:
            return (
                <IncidentLogNotificationField
                    answerLabel={translateString("notificationOfDataSubjects")}
                    givenDescriptionLabel={translateString("notificationMethod")}
                    notGivenDescriptionLabel={translateString("reasonForNotNotifying")}
                    answer={incidentLog?.affectedPeopleNotified}
                    dates={[
                        {
                            label: translateString("dateAndTimeOfNotification"),
                            date: incidentLog?.affectedPeopleNotifiedDate,
                            time: incidentLog?.affectedPeopleNotifiedTime,
                            onDateChange: (date) =>
                                onChange((entity) => {
                                    entity.affectedPeopleNotifiedDate = date;
                                }),
                            onTimeChange: (time) =>
                                onChange((entity) => {
                                    entity.affectedPeopleNotifiedTime = time;
                                }),
                        },
                    ]}
                    description={incidentLog?.affectedPeopleNotifiedDescription}
                    onAnswerChange={(answer) =>
                        onChange((entity) => {
                            if (changesNotificationDescriptionMeaning(entity.affectedPeopleNotified, answer)) {
                                entity.affectedPeopleNotifiedDescription = "";
                            }
                            entity.affectedPeopleNotified = answer;
                            if (answer !== IncidentLogNotificationAnswer.Yes) {
                                entity.affectedPeopleNotifiedDate = null;
                                entity.affectedPeopleNotifiedTime = null;
                            }
                        })
                    }
                    onDescriptionChange={(description) =>
                        onChange((entity) => {
                            entity.affectedPeopleNotifiedDescription = description;
                        })
                    }
                    isPreview={isPreview}
                    disabled={isDisabled}
                    errorText={getErrorText(IncidentLogStandardField.IncidentLogAffectedPeopleNotified)}
                />
            );
        case IncidentLogStandardField.IncidentLogReportedToAuthorities:
            return (
                <IncidentLogNotificationField
                    answerLabel={translateString("nis2ReportingToAuthorities")}
                    givenDescriptionLabel={translateString("nis2ReportingMethod")}
                    notGivenDescriptionLabel={translateString("justification")}
                    answer={incidentLog?.reportedToAuthorities}
                    dates={[
                        {
                            label: translateString("dateAndTimeOfEarlyWarning"),
                            date: incidentLog?.earlyWarningDate,
                            time: incidentLog?.earlyWarningTime,
                            onDateChange: (date) =>
                                onChange((entity) => {
                                    entity.earlyWarningDate = date;
                                }),
                            onTimeChange: (time) =>
                                onChange((entity) => {
                                    entity.earlyWarningTime = time;
                                }),
                        },
                        {
                            label: translateString("dateAndTimeOfIncidentNotification"),
                            date: incidentLog?.incidentNotificationDate,
                            time: incidentLog?.incidentNotificationTime,
                            onDateChange: (date) =>
                                onChange((entity) => {
                                    entity.incidentNotificationDate = date;
                                }),
                            onTimeChange: (time) =>
                                onChange((entity) => {
                                    entity.incidentNotificationTime = time;
                                }),
                        },
                        {
                            label: translateString("dateAndTimeOfFinalReport"),
                            date: incidentLog?.finalReportDate,
                            time: incidentLog?.finalReportTime,
                            onDateChange: (date) =>
                                onChange((entity) => {
                                    entity.finalReportDate = date;
                                }),
                            onTimeChange: (time) =>
                                onChange((entity) => {
                                    entity.finalReportTime = time;
                                }),
                        },
                    ]}
                    description={incidentLog?.reportedToAuthoritiesDescription}
                    onAnswerChange={(answer) =>
                        onChange((entity) => {
                            entity.reportedToAuthorities = answer;
                            if (answer !== IncidentLogNotificationAnswer.Yes) {
                                entity.earlyWarningDate = null;
                                entity.earlyWarningTime = null;
                                entity.incidentNotificationDate = null;
                                entity.incidentNotificationTime = null;
                                entity.finalReportDate = null;
                                entity.finalReportTime = null;
                            }
                        })
                    }
                    onDescriptionChange={(description) =>
                        onChange((entity) => {
                            entity.reportedToAuthoritiesDescription = description;
                        })
                    }
                    isPreview={isPreview}
                    disabled={isDisabled}
                    errorText={getErrorText(IncidentLogStandardField.IncidentLogReportedToAuthorities)}
                />
            );
        case IncidentLogStandardField.IncidentLogServiceRecipientsNotified:
            return (
                <IncidentLogNotificationField
                    answerLabel={translateString("notificationOfServiceRecipients")}
                    givenDescriptionLabel={translateString("nis2NotificationMethod")}
                    notGivenDescriptionLabel={translateString("justification")}
                    answer={incidentLog?.serviceRecipientsNotified}
                    dates={[
                        {
                            label: translateString("nis2DateAndTimeOfNotification"),
                            date: incidentLog?.serviceRecipientsNotifiedDate,
                            time: incidentLog?.serviceRecipientsNotifiedTime,
                            onDateChange: (date) =>
                                onChange((entity) => {
                                    entity.serviceRecipientsNotifiedDate = date;
                                }),
                            onTimeChange: (time) =>
                                onChange((entity) => {
                                    entity.serviceRecipientsNotifiedTime = time;
                                }),
                        },
                    ]}
                    description={incidentLog?.serviceRecipientsNotifiedDescription}
                    onAnswerChange={(answer) =>
                        onChange((entity) => {
                            entity.serviceRecipientsNotified = answer;
                            if (answer !== IncidentLogNotificationAnswer.Yes) {
                                entity.serviceRecipientsNotifiedDate = null;
                                entity.serviceRecipientsNotifiedTime = null;
                            }
                        })
                    }
                    onDescriptionChange={(description) =>
                        onChange((entity) => {
                            entity.serviceRecipientsNotifiedDescription = description;
                        })
                    }
                    isPreview={isPreview}
                    disabled={isDisabled}
                    errorText={getErrorText(IncidentLogStandardField.IncidentLogServiceRecipientsNotified)}
                />
            );
        case IncidentLogStandardField.IncidentLogInformationSecurityReportedToAuthorities:
            return (
                <IncidentLogNotificationField
                    answerLabel={translateString("nis2ReportingToAuthorities")}
                    givenDescriptionLabel={translateString("nis2ReportingMethod")}
                    notGivenDescriptionLabel={translateString("justification")}
                    answer={incidentLog?.informationSecurityReportedToAuthorities}
                    dates={[
                        {
                            label: translateString("dateAndTimeOfReporting"),
                            date: incidentLog?.informationSecurityReportedToAuthoritiesDate,
                            time: incidentLog?.informationSecurityReportedToAuthoritiesTime,
                            onDateChange: (date) =>
                                onChange((entity) => {
                                    entity.informationSecurityReportedToAuthoritiesDate = date;
                                }),
                            onTimeChange: (time) =>
                                onChange((entity) => {
                                    entity.informationSecurityReportedToAuthoritiesTime = time;
                                }),
                        },
                    ]}
                    description={incidentLog?.informationSecurityReportedToAuthoritiesDescription}
                    onAnswerChange={(answer) =>
                        onChange((entity) => {
                            if (changesNotificationDescriptionMeaning(entity.informationSecurityReportedToAuthorities, answer)) {
                                entity.informationSecurityReportedToAuthoritiesDescription = "";
                            }
                            entity.informationSecurityReportedToAuthorities = answer;
                            if (answer !== IncidentLogNotificationAnswer.Yes) {
                                entity.informationSecurityReportedToAuthoritiesDate = null;
                                entity.informationSecurityReportedToAuthoritiesTime = null;
                            }
                        })
                    }
                    onDescriptionChange={(description) =>
                        onChange((entity) => {
                            entity.informationSecurityReportedToAuthoritiesDescription = description;
                        })
                    }
                    isPreview={isPreview}
                    disabled={isDisabled}
                    errorText={getErrorText(IncidentLogStandardField.IncidentLogInformationSecurityReportedToAuthorities)}
                />
            );
        case IncidentLogStandardField.IncidentLogAffectedPartiesNotified:
            return (
                <IncidentLogNotificationField
                    answerLabel={translateString("notificationOfAffectedParties")}
                    givenDescriptionLabel={translateString("nis2NotificationMethod")}
                    notGivenDescriptionLabel={translateString("justification")}
                    answer={incidentLog?.affectedPartiesNotified}
                    dates={[
                        {
                            label: translateString("nis2DateAndTimeOfNotification"),
                            date: incidentLog?.affectedPartiesNotifiedDate,
                            time: incidentLog?.affectedPartiesNotifiedTime,
                            onDateChange: (date) =>
                                onChange((entity) => {
                                    entity.affectedPartiesNotifiedDate = date;
                                }),
                            onTimeChange: (time) =>
                                onChange((entity) => {
                                    entity.affectedPartiesNotifiedTime = time;
                                }),
                        },
                    ]}
                    description={incidentLog?.affectedPartiesNotifiedDescription}
                    onAnswerChange={(answer) =>
                        onChange((entity) => {
                            if (changesNotificationDescriptionMeaning(entity.affectedPartiesNotified, answer)) {
                                entity.affectedPartiesNotifiedDescription = "";
                            }
                            entity.affectedPartiesNotified = answer;
                            if (answer !== IncidentLogNotificationAnswer.Yes) {
                                entity.affectedPartiesNotifiedDate = null;
                                entity.affectedPartiesNotifiedTime = null;
                            }
                        })
                    }
                    onDescriptionChange={(description) =>
                        onChange((entity) => {
                            entity.affectedPartiesNotifiedDescription = description;
                        })
                    }
                    isPreview={isPreview}
                    disabled={isDisabled}
                    errorText={getErrorText(IncidentLogStandardField.IncidentLogAffectedPartiesNotified)}
                />
            );
        case IncidentLogStandardField.IncidentLogDocuments:
            // Documents are saved against the incident id through their own endpoints, so they are not part of the model.
            if (isPreview) {
                return <DocumentsEmptyState />;
            }
            return (
                <IncidentDocuments
                    incidentId={incidentLogId}
                    incidentName={incidentLog?.name}
                    emptyStateWrappedInPaper
                    errorText={getErrorText(IncidentLogStandardField.IncidentLogDocuments)}
                />
            );
        default:
            return exhaustiveGuard(field.standardFieldIdentifier);
    }
};

export const IncidentLogCustomFieldRenderer: React.FC<IncidentLogCustomFieldProps> = ({
    incidentLog,
    incidentLogId,
    field,
    onChange,
    isPreview,
    isLoading = false,
    validationErrors,
    computedFieldEditing,
}) => {
    if (!field.customInputFieldType) {
        return null;
    }

    const isDisabled = isPreview || field.disabled;
    const value = (incidentLog?.customFieldValues ?? []).find((x) => x.customInputFieldId == field.customInputFieldId)?.value ?? "";
    const errorText = validationErrors.find((v) => v.field === field.id)?.error;

    switch (field.customInputFieldType) {
        case InputFieldTypeEnum.TextField:
            return (
                <CustomTextField
                    isLoading={isLoading}
                    entityId={incidentLogId}
                    entityApiPath={incidentLogCustomFieldApiPath}
                    customInputFieldId={field.customInputFieldId!}
                    fieldName={field.customInputFieldName!}
                    fieldDescription={field.customInputFieldDescription}
                    value={value as string}
                    onChange={onChange}
                    isDisabled={isDisabled}
                    isPreview={isPreview}
                    errorText={errorText}
                />
            );
        case InputFieldTypeEnum.RichTextField:
            return (
                <CustomRichTextField
                    isLoading={isLoading}
                    entityId={incidentLogId}
                    entityApiPath={incidentLogCustomFieldApiPath}
                    customInputFieldId={field.customInputFieldId!}
                    fieldName={field.customInputFieldName!}
                    fieldDescription={field.customInputFieldDescription}
                    value={value as string}
                    onChange={onChange}
                    isDisabled={isDisabled}
                    isPreview={isPreview}
                    errorText={errorText}
                />
            );
        case InputFieldTypeEnum.CountryField:
            return (
                <CustomCountryField
                    isLoading={isLoading}
                    entityId={incidentLogId}
                    entityApiPath={incidentLogCustomFieldApiPath}
                    customInputFieldId={field.customInputFieldId!}
                    fieldName={field.customInputFieldName!}
                    fieldDescription={field.customInputFieldDescription}
                    value={value as string}
                    onChange={onChange}
                    isDisabled={isDisabled}
                    isPreview={isPreview}
                    errorText={errorText}
                />
            );
        case InputFieldTypeEnum.SingleDropDown:
            return (
                <CustomSingleSelectField
                    isLoading={isLoading}
                    entityId={incidentLogId}
                    entityApiPath={incidentLogCustomFieldApiPath}
                    customInputFieldId={field.customInputFieldId!}
                    fieldName={field.customInputFieldName!}
                    fieldDescription={field.customInputFieldDescription}
                    value={value as string}
                    onChange={onChange}
                    isDisabled={isDisabled}
                    isPreview={isPreview}
                    errorText={errorText}
                />
            );
        case InputFieldTypeEnum.MultiDropDown:
            return (
                <CustomMultiSelectField
                    isLoading={isLoading}
                    entityId={incidentLogId}
                    entityApiPath={incidentLogCustomFieldApiPath}
                    customInputFieldId={field.customInputFieldId!}
                    fieldName={field.customInputFieldName!}
                    fieldDescription={field.customInputFieldDescription}
                    values={value as string[]}
                    onChange={onChange}
                    isDisabled={isDisabled}
                    isPreview={isPreview}
                    errorText={errorText}
                />
            );
        case InputFieldTypeEnum.DateField:
            return (
                <CustomDateField
                    isLoading={isLoading}
                    entityId={incidentLogId}
                    entityApiPath={incidentLogCustomFieldApiPath}
                    customInputFieldId={field.customInputFieldId!}
                    fieldName={field.customInputFieldName!}
                    fieldDescription={field.customInputFieldDescription}
                    value={value as Date | null}
                    onChange={onChange}
                    isDisabled={isDisabled}
                    isPreview={isPreview}
                    errorText={errorText}
                />
            );
        case InputFieldTypeEnum.NumberField:
            return (
                <CustomNumberField
                    isLoading={isLoading}
                    entityId={incidentLogId}
                    entityApiPath={incidentLogCustomFieldApiPath}
                    customInputFieldId={field.customInputFieldId!}
                    fieldName={field.customInputFieldName!}
                    fieldDescription={field.customInputFieldDescription}
                    value={value as string}
                    onChange={onChange}
                    isDisabled={isDisabled}
                    isPreview={isPreview}
                    errorText={errorText}
                />
            );
        case InputFieldTypeEnum.AmountField:
            return (
                <CustomAmountField
                    isLoading={isLoading}
                    entityId={incidentLogId}
                    entityApiPath={incidentLogCustomFieldApiPath}
                    customInputFieldId={field.customInputFieldId!}
                    fieldName={field.customInputFieldName!}
                    fieldDescription={field.customInputFieldDescription}
                    value={value as { Amount: number; CurrencyCode: string }}
                    onChange={onChange}
                    isDisabled={isDisabled}
                    isPreview={isPreview}
                    errorText={errorText}
                />
            );
        case InputFieldTypeEnum.CheckboxField:
            return (
                <CustomCheckbox
                    isLoading={isLoading}
                    entityId={incidentLogId}
                    entityApiPath={incidentLogCustomFieldApiPath}
                    customInputFieldId={field.customInputFieldId!}
                    fieldName={field.customInputFieldName!}
                    fieldDescription={field.customInputFieldDescription}
                    value={value as boolean}
                    onChange={onChange}
                    isDisabled={isDisabled}
                    isPreview={isPreview}
                    errorText={errorText}
                />
            );
        case InputFieldTypeEnum.LegalEntityField:
            return (
                <CustomLegalEntityField
                    isLoading={isLoading}
                    entityId={incidentLogId}
                    entityApiPath={incidentLogCustomFieldApiPath}
                    customInputFieldId={field.customInputFieldId!}
                    fieldName={field.customInputFieldName!}
                    fieldDescription={field.customInputFieldDescription}
                    values={value as string[]}
                    onChange={onChange}
                    isDisabled={isDisabled}
                    isPreview={isPreview}
                    errorText={errorText}
                />
            );
        case InputFieldTypeEnum.UserField:
            return (
                <CustomUserField
                    isLoading={isLoading}
                    entityId={incidentLogId}
                    entityApiPath={incidentLogCustomFieldApiPath}
                    customInputFieldId={field.customInputFieldId!}
                    fieldName={field.customInputFieldName!}
                    fieldDescription={field.customInputFieldDescription}
                    values={value as string[]}
                    onChange={onChange}
                    isDisabled={isDisabled}
                    isPreview={isPreview}
                    errorText={errorText}
                />
            );
        case InputFieldTypeEnum.CalculatedField:
            return (
                <CustomCalculatedField
                    field={field}
                    isLoading={isLoading}
                    fieldDescription={field.customInputFieldDescription}
                    isPreview={isPreview}
                    errorText={errorText}
                    templateEntityType={TemplateEntityType.IncidentLog}
                    computedFieldEditing={computedFieldEditing}
                    entityTemplateId={incidentLog?.entityTemplateId}
                    entity={{ customFieldValues: incidentLog?.customFieldValues ?? [] }}
                />
            );

        case InputFieldTypeEnum.DatePeriodField:
            return (
                <CustomDatePeriodField
                    field={field}
                    isLoading={isLoading}
                    fieldDescription={field.customInputFieldDescription}
                    isPreview={isPreview}
                    errorText={errorText}
                    templateEntityType={TemplateEntityType.IncidentLog}
                    computedFieldEditing={computedFieldEditing}
                    entity={{ customFieldValues: incidentLog?.customFieldValues ?? [] }}
                />
            );
        default:
            return exhaustiveGuard(field.customInputFieldType);
    }
};

export const IncidentLogFieldRenderer: React.FC<IncidentLogFieldRendererProps> = (props) => {
    if (props.field.standardFieldIdentifier) {
        return (
            <IncidentLogStandardFieldRenderer
                field={props.field}
                incidentLog={props.incidentLog}
                incidentLogId={props.incidentLogId ?? ""}
                onChange={props.onChange}
                validationErrors={props.validationErrors ?? []}
                isPreview={props.isPreview}
                isLoading={props.isLoading}
            />
        );
    }

    return (
        <IncidentLogCustomFieldRenderer
            incidentLog={props.incidentLog}
            field={props.field}
            onChange={props.onCustomFieldChange}
            isPreview={props.isPreview}
            incidentLogId={props.incidentLogId ?? ""}
            isLoading={props.isLoading}
            validationErrors={props.validationErrors ?? []}
            computedFieldEditing={props.computedFieldEditing}
        />
    );
};
